/**
 * Tools — the whole manifest a person turns on, tool by tool.
 *
 * Each tool: its name and description, its Settings and Inputs, what
 * it reads, what it writes (a schema picked by name, stored by id), what it does
 * outside ofSelf, and whether it can be undone. Then what holds for every tool:
 * Ask every run, Removes what it reads, Keys (the public key) and Sign in.
 *
 * A tool's key that is absent means "the plugin's own", so a tool is edited by
 * changing only the keys someone touched; everything else round-trips as loaded.
 */
import { useState } from 'react';
import { Toggle } from '@ofself/shell';

import { Button, ConfirmDialog } from '../../components/common';
import FieldBuilder from './FieldBuilder';
import { Field, SchemaPicker, useSchemas } from './bits';
import {
  ALWAYS_IRREVERSIBLE, EFFECT_KINDS, VERBS, errorsAt, irreversible, readOf, toolGaps, withSchema,
} from './manifest';
import { download, isPublicKey, makeKeyPair, privateKeyFile } from './keys';

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/* ── the lists inside a tool ──────────────────────────────────────────────── */

function Reads({ value, onChange, schemas, disabled }) {
  const reads = value || [];
  const set = (i, next) => onChange(reads.map((r, n) => (n === i ? next : r)));
  return (
    <div className="pw__rows">
      {reads.map((r, i) => (
        <div key={i} className="pw__rowline">
          {r.type === 'plugin_output' ? (
            <span className="pw__line">What {r.plugin_name || 'another plugin'} makes</span>
          ) : (
            <SchemaPicker value={r.type === 'schema' ? r.schema_id : null} schemas={schemas} disabled={disabled}
                          anyLabel="Any node" onChange={(id) => set(i, readOf(r, id))} />
          )}
          <button type="button" className="pw__x" aria-label="Remove" disabled={disabled}
                  onClick={() => onChange(reads.filter((_, n) => n !== i))}>×</button>
        </div>
      ))}
      <button type="button" className="pw__add" disabled={disabled}
              onClick={() => onChange([...reads, { type: 'any' }])}>Add a read</button>
    </div>
  );
}

function Writes({ value, onChange, schemas, disabled }) {
  const writes = value || [];
  const set = (i, next) => onChange(writes.map((w, n) => (n === i ? next : w)));
  return (
    <div className="pw__rows">
      {writes.map((w, i) => (
        <div key={i} className="pw__rowline">
          <select className="pw__in pw__in--verb" value={w.action} disabled={disabled}
                  onChange={(e) => set(i, { ...w, action: e.target.value })}>
            {VERBS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
          <SchemaPicker value={w.schema_id} schemas={schemas} disabled={disabled}
                        anyLabel="Any schema" onChange={(id) => set(i, withSchema(w, id))} />
          <button type="button" className="pw__x" aria-label="Remove" disabled={disabled}
                  onClick={() => onChange(writes.filter((_, n) => n !== i))}>×</button>
        </div>
      ))}
      <button type="button" className="pw__add" disabled={disabled}
              onClick={() => onChange([...writes, { action: 'create' }])}>Add a write</button>
    </div>
  );
}

function Outside({ value, onChange, disabled, errors }) {
  const effects = value || [];
  const set = (i, patch) => onChange(effects.map((e, n) => (n === i ? { ...e, ...patch } : e)));
  return (
    <div className="pw__rows">
      {effects.map((e, i) => {
        const forced = ALWAYS_IRREVERSIBLE.includes(e.kind);
        const errs = errors.filter((x) => x.index === i);
        return (
          <div key={i} className={`pw__effect${errs.length ? ' is-bad' : ''}`}>
            <div className="pw__rowline">
              <select className="pw__in pw__in--verb" value={e.kind} disabled={disabled}
                      onChange={(ev) => set(i, { kind: ev.target.value })}>
                {EFFECT_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <input className="pw__in" value={e.description || ''} disabled={disabled}
                     placeholder={EFFECT_KINDS.find((k) => k.id === e.kind)?.text || 'What it does'}
                     aria-label="What a person reads"
                     onChange={(ev) => set(i, { description: ev.target.value })} />
              <button type="button" className="pw__x" aria-label="Remove" disabled={disabled}
                      onClick={() => onChange(effects.filter((_, n) => n !== i))}>×</button>
            </div>
            <Toggle
              checked={forced || !e.reversible}
              disabled={disabled || forced}
              onChange={(ev) => set(i, { reversible: !ev.target.checked })}
              label="Can't be undone"
            />
            {errs.map((x, n) => <span key={n} className="pw__err">{x.message}</span>)}
          </div>
        );
      })}
      <button type="button" className="pw__add" disabled={disabled}
              onClick={() => onChange([...effects, { kind: 'send', description: '', reversible: false }])}>
        Add something it does outside ofSelf
      </button>
    </div>
  );
}

/** Errors under `prefix` that name an effect: `{index, message}`. */
const effectErrors = (errors, prefix) => errorsAt(errors, prefix).map((e) => {
  const m = new RegExp(`^${prefix.replace(/[[\].]/g, '\\$&')}\\.effects\\[(\\d+)\\]`).exec(e.field);
  return m ? { index: Number(m[1]), message: e.message } : null;
}).filter(Boolean);

/* ── one tool ─────────────────────────────────────────────────────────────── */

function ToolCard({ tool, index, draft, open, onOpen, onChange, onRemove, isNew, schemas, disabled, errors, plain }) {
  const prefix = plain ? '' : `tools[${index}]`;
  const errs = plain ? errors.filter((e) => ['effects', 'input_parameters', 'node_requirements', 'output_declaration']
    .some((k) => e.field === k || e.field?.startsWith(`${k}[`) || e.field?.startsWith(`${k}.`))) : errorsAt(errors, prefix);
  const gaps = plain ? [] : toolGaps(tool, draft);
  const undone = (tool.effects || []).some(irreversible);
  const set = (patch) => onChange({ ...tool, ...patch });
  const t = draft.execution_type;
  const effErrs = plain
    ? errs.map((e) => { const m = /^effects\[(\d+)\]/.exec(e.field || ''); return m ? { index: Number(m[1]), message: e.message } : null; }).filter(Boolean)
    : effectErrors(errors, prefix);
  const other = errs.filter((e) => !/effects\[\d+\]/.test(e.field || ''));

  return (
    <li className={`pw__tool${open ? ' is-open' : ''}${errs.length ? ' is-bad' : ''}`}>
      <button type="button" className="pw__tool-head" onClick={onOpen} aria-expanded={open}>
        <span className="pw__tool-name">{plain ? (draft.name || 'This plugin') : (tool.name || <em>No name</em>)}</span>
        {undone ? <span className="pw__mark">Can't be undone</span> : null}
        <span className="pw__tool-state">
          {errs.length ? `${errs.length} to fix` : gaps.length ? `${gaps.length} left` : 'Done'}
        </span>
        {!plain && tool.tool_id ? <code className="pw__code">{tool.tool_id}</code> : null}
      </button>
      {!open && tool.description ? <p className="pw__tool-desc">{tool.description}</p> : null}
      {open ? (
        <div className="pw__tool-body">
          {other.map((e, i) => <p key={i} className="pw__err">{e.message}</p>)}
          {!plain ? (
            <>
              <div className="pw__grid">
                <Field label="Name">
                  <input className="pw__in" value={tool.name || ''} disabled={disabled}
                         onChange={(e) => set({ name: e.target.value, ...(isNew ? { tool_id: slug(e.target.value) || tool.tool_id } : {}) })} />
                </Field>
                {t === 'mcp' ? (
                  <Field label="The server's name for it">
                    <input className="pw__in pw__in--mono" value={tool.mcp_tool_name || ''} disabled={disabled}
                           onChange={(e) => set({ mcp_tool_name: e.target.value })} />
                  </Field>
                ) : t === 'endpoint' && (draft.plugin_endpoint_base_url || tool.endpoint_path) ? (
                  <Field label="Path · after the base URL">
                    <input className="pw__in pw__in--mono" value={tool.endpoint_path || ''} disabled={disabled}
                           placeholder="/path"
                           onChange={(e) => {
                             const next = { ...tool, endpoint_path: e.target.value };
                             if (!e.target.value) delete next.endpoint_path;
                             onChange(next);
                           }} />
                  </Field>
                ) : null}
              </div>
              <Field label="Description">
                <textarea className="pw__in" rows={2} value={tool.description || ''} disabled={disabled}
                          onChange={(e) => set({ description: e.target.value })} />
              </Field>
            </>
          ) : null}

          <div className="pw__part">
            <h4 className="pw__h pw__h--sub">Settings and inputs</h4>
            <FieldBuilder value={tool.input_parameters || []} disabled={disabled}
                          schemas={schemas.list} canHoldCredentials={t === 'endpoint'}
                          onChange={(v) => set({ input_parameters: v })} />
          </div>
          <div className="pw__part">
            <h4 className="pw__h pw__h--sub">Reads</h4>
            <Reads value={tool.node_requirements} schemas={schemas} disabled={disabled}
                   onChange={(v) => set({ node_requirements: v })} />
          </div>
          <div className="pw__part">
            <h4 className="pw__h pw__h--sub">Writes</h4>
            <Writes value={tool.output_declaration} schemas={schemas} disabled={disabled}
                    onChange={(v) => set({ output_declaration: v })} />
          </div>
          <div className="pw__part">
            <h4 className="pw__h pw__h--sub">Outside ofSelf</h4>
            <Outside value={tool.effects} disabled={disabled} errors={effErrs}
                     onChange={(v) => set({ effects: v })} />
          </div>
          {!plain ? (
            <div className="pw__actions">
              <Button variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>Remove this tool</Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/* ── what holds for every tool ────────────────────────────────────────────── */

function Credentials({ draft, set, disabled, errors, pluginName }) {
  const [confirm, setConfirm] = useState(false);
  const [made, setMade] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = draft.enc_public_key || '';
  const bad = key && !isPublicKey(key);

  const make = async () => {
    setConfirm(false);
    setBusy(true);
    setError('');
    try {
      const pair = await makeKeyPair();
      const file = privateKeyFile(pluginName, pair.privateKey);
      download(file.filename, file.text);
      set({ enc_public_key: pair.publicKey });
      setMade(file.filename);
    } catch (e) {
      setError(e.message || 'Could not make a key.');
    }
    setBusy(false);
  };

  return (
    <div className="pw__part">
      <h4 className="pw__h pw__h--sub">Keys</h4>
      <Field label="Public key" errors={[
        ...(bad ? ['This isn\'t a P-256 public key. Paste one, or make one here.'] : []),
        ...errorsAt(errors, 'enc_public_key').map((e) => e.message),
        ...(error ? [error] : []),
      ]}>
        <div className="pw__inline">
          <input className="pw__in pw__in--mono" value={key} disabled={disabled}
                 placeholder="Paste the public half, or make a key"
                 onChange={(e) => { set({ enc_public_key: e.target.value.trim() }); setMade(null); }} />
          <Button variant="secondary" onClick={() => (key ? setConfirm(true) : make())} loading={busy} disabled={disabled}>
            Make a key
          </Button>
        </div>
      </Field>
      {made ? (
        <p className="pw__line pw__note">
          The private key downloaded as <code className="pw__code">{made}</code>. It isn't kept here.
          Set it as <code className="pw__code">PARADIGM_PLUGIN_PRIVATE_KEY</code> where your handler runs, then save.
        </p>
      ) : null}
      <ConfirmDialog
        isOpen={confirm}
        title="Replace the key?"
        message="Keys people already pasted were locked to the old public key. They'll have to paste them again."
        confirmLabel="Make a new key"
        cancelLabel="Keep this one"
        onConfirm={make}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
}

const OAUTH_FIELDS = [
  ['name', 'Provider', 'Example'],
  ['authorize_url', 'Sign-in URL', 'https://auth.example.com/authorize'],
  ['token_url', 'Token URL', 'https://auth.example.com/token'],
  ['client_id', 'Client ID', '1234.apps.example.com'],
];

function Account({ draft, set, disabled, errors }) {
  const cfg = draft.oauth_provider;
  const editing = cfg && typeof cfg === 'object';
  // The plugin's read doesn't carry its provider; a request that sends the
  // account's token is the sign that it has one.
  const usesAccount = JSON.stringify(draft.request_spec || {}).includes('{{oauth_token}}');
  const change = (patch) => {
    const next = { ...(cfg || {}), ...patch };
    for (const k of Object.keys(patch)) if (patch[k] === '' || patch[k] === undefined) delete next[k];
    set({ oauth_provider: next });
  };
  return (
    <div className="pw__part">
      <h4 className="pw__h pw__h--sub">Sign in</h4>
      {cfg === null ? (
        <div className="pw__inline">
          <span className="pw__line">The provider is removed when you save.</span>
          <Button variant="ghost" size="sm" onClick={() => set({ oauth_provider: undefined })}>Undo</Button>
        </div>
      ) : !editing ? (
        <div className="pw__actions">
          <Button variant="secondary" size="sm" disabled={disabled}
                  onClick={() => set({ oauth_provider: { scopes: [] } })}>Set the provider</Button>
          {usesAccount ? (
            <Button variant="ghost" size="sm" disabled={disabled}
                    onClick={() => set({ oauth_provider: null })}>Remove the provider</Button>
          ) : null}
        </div>
      ) : (
        <div className="pw__fields">
          {errorsAt(errors, 'oauth_provider').map((e, i) => <span key={i} className="pw__err">{e.message}</span>)}
          <div className="pw__grid">
            {OAUTH_FIELDS.map(([k, label, ph]) => (
              <Field key={k} label={label}>
                <input className="pw__in" value={cfg[k] || ''} placeholder={ph} disabled={disabled}
                       onChange={(e) => change({ [k]: e.target.value })} />
              </Field>
            ))}
            <Field label="Scopes · separated by spaces">
              <input className="pw__in pw__in--mono" disabled={disabled}
                     value={(cfg.scopes || []).join(' ')}
                     onChange={(e) => change({ scopes: e.target.value.split(/[\s,]+/).filter(Boolean) })} />
            </Field>
            <Field label="Client secret · empty keeps the one stored">
              <input className="pw__in" type="password" autoComplete="new-password" disabled={disabled}
                     value={cfg.client_secret || ''}
                     onChange={(e) => change({ client_secret: e.target.value })} />
            </Field>
          </div>
          <div className="pw__actions">
            <Button variant="ghost" size="sm" onClick={() => set({ oauth_provider: undefined })}>Leave it as it is</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── the step ─────────────────────────────────────────────────────────────── */

export default function ToolsStep({ draft, setDraft, base, disabled, errors = [] }) {
  const schemas = useSchemas();
  const tools = draft.tools || [];
  const [open, setOpen] = useState(tools.length ? null : 0);
  const baseIds = new Set((base?.tools || []).map((t) => t.tool_id));
  const set = (patch) => setDraft({ ...draft, ...patch });
  const setTool = (i, next) => set({ tools: tools.map((t, n) => (n === i ? next : t)) });

  const addTool = () => {
    const n = tools.length + 1;
    let id = `tool_${n}`;
    while (tools.some((t) => t.tool_id === id)) id = `${id}_`;
    set({ tools: [...tools, { tool_id: id, name: '', description: '', input_parameters: [], output_declaration: [] }] });
    setOpen(tools.length);
  };

  return (
    <div className="pw__step">
      <section className="pw__sec">
        <h2 className="pw__h">Tools · {tools.length || 1}</h2>
        <p className="pw__line">
          Each tool declares what it reads and writes. A plugin with no tools is one tool, named after the plugin.
        </p>
        <ol className="pw__tools">
          {tools.length ? tools.map((t, i) => (
            <ToolCard key={i} tool={t} index={i} draft={draft} open={open === i}
                      onOpen={() => setOpen(open === i ? null : i)}
                      onChange={(next) => setTool(i, next)}
                      onRemove={() => { set({ tools: tools.filter((_, n) => n !== i) }); setOpen(null); }}
                      isNew={!baseIds.has(t.tool_id)}
                      schemas={schemas} disabled={disabled} errors={errors} />
          )) : (
            <ToolCard plain tool={draft} index={0} draft={draft} open={open === 0}
                      onOpen={() => setOpen(open === 0 ? null : 0)}
                      onChange={(next) => set({
                        input_parameters: next.input_parameters,
                        node_requirements: next.node_requirements,
                        output_declaration: next.output_declaration,
                        effects: next.effects,
                      })}
                      schemas={schemas} disabled={disabled} errors={errors} />
          )}
        </ol>
        <div className="pw__actions">
          <Button variant="secondary" onClick={addTool} disabled={disabled}>Add a tool</Button>
        </div>
      </section>

      <section className="pw__sec">
        <h2 className="pw__h">Every tool</h2>
        <div className="pw__switchrows">
          <Toggle checked={!!draft.requires_consent} disabled={disabled}
                  onChange={(e) => set({ requires_consent: e.target.checked })} label="Ask every run" />
          <Toggle checked={!!draft.remove_source} disabled={disabled}
                  onChange={(e) => set({ remove_source: e.target.checked })} label="Removes what it reads" />
        </div>
        {draft.execution_type === 'endpoint' ? (
          <Credentials draft={draft} set={set} disabled={disabled} errors={errors} pluginName={draft.name} />
        ) : null}
        <Account draft={draft} set={set} disabled={disabled} errors={errors} />
      </section>
    </div>
  );
}
